import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Building2,
  CheckCircle2,
  Lightbulb,
  Sparkles,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2 }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-gray-800 sm:text-[0.9375rem]">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Business & corporate law in Ghana. */
export const BusinessCorporateOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_24px_64px_-32px_rgba(15,23,42,0.2)] sm:rounded-[1.85rem]"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-50 via-[#f7faf9] to-emerald-50/35"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-[5px]"
        style={{ background: `linear-gradient(180deg, ${color}, ${color}55)` }}
        aria-hidden
      />
      <div className="relative grid lg:grid-cols-12">
        <div className="flex flex-col justify-center px-6 py-10 sm:p-10 lg:col-span-7 lg:pl-11">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-[0.32em]" style={{ color }}>
              LEGAL · BUSINESS &amp; CORPORATE LAW (GHANA)
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline" aria-hidden />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/80 px-3 py-1 text-[11px] font-bold text-slate-600">
              <Briefcase size={13} style={{ color }} aria-hidden />
              {data.funnelTagline}
            </span>
          </div>

          <h2 className="max-w-2xl text-[1.5rem] font-black leading-[1.2] tracking-tight text-slate-950 sm:text-[1.85rem]">
            {data.introduction.headline}
          </h2>
          <p className="mt-4 max-w-prose text-[0.9375rem] leading-[1.8] text-slate-600 sm:text-base">
            {data.introduction.lead}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {data.focusAreas.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-slate-200/90 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-700"
              >
                {tag}
              </span>
            ))}
          </div>

          <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">
            {data.introduction.manyPeopleLead}
          </p>
          <div className="mt-3 rounded-xl border border-slate-200/80 bg-slate-50/60 p-5 sm:p-6">
            <ListBlock items={data.introduction.manyPeople} color={color} />
          </div>

          <div className="mt-6 rounded-xl border border-slate-900/[0.06] bg-slate-900/[0.03] px-5 py-4 sm:px-6">
            <p className="text-sm font-black text-slate-900">{data.introduction.resultLead}</p>
            <ul className="mt-3 space-y-2">
              {data.introduction.resultBullets.map((r) => (
                <li key={r} className="text-sm font-semibold text-slate-800">
                  • {r}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm font-bold leading-relaxed text-slate-900">{data.introduction.closing}</p>
          </div>
        </div>

        <div className="relative min-h-[16rem] border-t border-slate-100 lg:col-span-5 lg:border-l lg:border-t-0 lg:min-h-0">
          <img src={data.galleryImages[0]} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-md">
              <Building2 size={14} className="text-white" aria-hidden />
              <span className="text-[10px] font-black uppercase tracking-[0.24em] text-white/95">Founders &amp; SMEs</span>
            </div>
            <p className="mt-3 max-w-sm text-lg font-black leading-snug text-white [text-wrap:balance]">
              {data.focusAreas.join(" · ")}
            </p>
          </div>
        </div>
      </div>
    </motion.section>

    <section>
      <SectionHead
        eyebrow="ORC &amp; structure"
        title={data.businessRegistration.title}
        subtitle={data.businessRegistration.whyHeading}
        color={color}
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-sm font-bold text-slate-900">{data.businessRegistration.whyIntro}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-700">
            {data.businessRegistration.whyBullets.map((b) => (
              <li key={b}>• {b}</li>
            ))}
          </ul>
          <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50 p-5">
            <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.businessRegistration.whereHeading}</p>
            <p className="mt-2 text-sm font-semibold text-slate-700">{data.businessRegistration.whereLead}</p>
            <p className="mt-3 text-lg font-black text-slate-950">{data.businessRegistration.orcLine}</p>
          </div>
          <div className="mt-8">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {data.businessRegistration.structuresHeading}
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {data.businessRegistration.structures.map((s) => (
                <div
                  key={s}
                  className="rounded-lg border border-slate-200/90 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 shadow-sm"
                >
                  • {s}
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm font-bold text-slate-900">{data.businessRegistration.structureImpactLead}</p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {data.businessRegistration.structureImpact.map((x) => (
                <li
                  key={x}
                  className="rounded-full border px-3 py-1 text-xs font-bold"
                  style={{ borderColor: `${color}44`, background: `${color}10`, color }}
                >
                  {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/90 bg-white p-6">
            <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.businessRegistration.processHeading}</p>
            <ol className="mt-6 space-y-6">
              {data.businessRegistration.steps.map((st) => (
                <li key={st.number} className="relative pl-0">
                  <div className="flex gap-4">
                    <span
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-black shadow-md"
                      style={{ background: color, color: foregroundOnSolidAccent(color) }}
                    >
                      {st.number}
                    </span>
                    <div>
                      <p className="font-black text-slate-950">{st.title}</p>
                      {st.body ? (
                        <p className="mt-2 text-sm font-semibold text-slate-600">{st.body}</p>
                      ) : null}
                      {st.bullets?.length ? (
                        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
                          {st.bullets.map((bb) => (
                            <li key={bb}>• {bb}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-xl border border-amber-200/80 bg-amber-50/55 p-6">
            <p className="flex items-center gap-2 text-sm font-black text-amber-950">
              <AlertTriangle size={16} className="shrink-0 text-amber-600" aria-hidden />
              {data.businessRegistration.mistakesHeading}
            </p>
            <ul className="mt-3 space-y-2 text-sm font-semibold text-amber-950/95">
              {data.businessRegistration.mistakes.map((m) => (
                <li key={m}>• {m}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="rounded-xl border border-slate-900/[0.12] bg-gradient-to-br from-[#0f172a] via-slate-900 to-emerald-950/50 p-6 text-white sm:p-10">
      <div className="mb-4 flex items-center gap-2">
        <div className="h-[3px] w-12 rounded-full" style={{ background: color }} aria-hidden />
        <span className="text-[10px] font-black uppercase tracking-[0.28em] text-white/50">Compliance culture</span>
      </div>
      <h3 className="text-xl font-black sm:text-2xl">{data.compliance.title}</h3>
      <p className="mt-3 max-w-prose text-sm font-semibold text-white/70">{data.compliance.opener}</p>
      <p className="mt-2 text-base font-black text-white">{data.compliance.stayCompliantLead}</p>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
            {data.compliance.whatMeansHeading}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-white/85">{data.compliance.whatMeans}</p>
          <p className="mt-8 text-[10px] font-black uppercase tracking-[0.2em] text-white/50">{data.compliance.keyHeading}</p>
          <div className="mt-4 space-y-4">
            {data.compliance.requirements.map((req) => (
              <div key={req.title} className="rounded-xl border border-white/10 bg-white/[0.06] p-4">
                <p className="text-sm font-black text-white">{req.title}</p>
                <ul className="mt-2 space-y-1 text-sm font-semibold text-white/75">
                  {req.bullets.map((b) => (
                    <li key={b}>• {b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col justify-between gap-6">
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.07] p-5">
            <p className="text-xs font-black uppercase tracking-wider text-emerald-200">{data.compliance.whyMattersHeading}</p>
            <ul className="mt-3 space-y-2 text-sm font-semibold text-emerald-50/95">
              {data.compliance.whyMatters.map((w) => (
                <li key={w}>• {w}</li>
              ))}
            </ul>
          </div>
          <p className="text-sm font-bold leading-relaxed text-white/80">{data.compliance.whyFooter}</p>
        </div>
      </div>
    </section>

    <section className="grid gap-6 xl:grid-cols-12">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8 xl:col-span-7">
        <SectionHead eyebrow="Every deal" title={data.contracts.title} subtitle={data.contracts.whatHeading} color={color} />
        <p className="text-sm font-semibold text-slate-700">{data.contracts.whatBody}</p>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.contracts.commonHeading}</p>
        <ul className="mt-2 space-y-1.5 text-sm font-semibold text-slate-800">
          {data.contracts.common.map((c) => (
            <li key={c}>• {c}</li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.contracts.elementsHeading}
        </p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
          {data.contracts.elements.map((e) => (
            <li key={e}>• {e}</li>
          ))}
        </ul>
      </div>
      <div className="space-y-4 xl:col-span-5">
        <div className="rounded-xl border border-rose-100 bg-rose-50/65 p-6">
          <p className="text-sm font-black text-rose-950">{data.contracts.problemsHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-rose-900/95">
            {data.contracts.problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-gradient-to-br from-white to-emerald-50/30 p-6">
          <div className="flex items-center gap-2">
            <Lightbulb size={18} style={{ color }} aria-hidden />
            <p className="text-sm font-black text-slate-950">{data.contracts.bestPracticeHeading}</p>
          </div>
          <p className="mt-3 text-xs font-black uppercase tracking-wider text-slate-500">{data.contracts.bestPracticeLead}</p>
          <ul className="mt-2 space-y-2 text-sm font-bold text-slate-800">
            {data.contracts.bestPractice.map((b) => (
              <li key={b}>• {b}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Co-founders" title={data.partnershipDisputes.title} subtitle={data.partnershipDisputes.intro} color={color} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.partnershipDisputes.causesHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.partnershipDisputes.causes.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-slate-50/80 p-6">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.partnershipDisputes.preventHeading}
          </p>
          <p className="mt-3 text-sm font-bold text-slate-900">{data.partnershipDisputes.agreementLead}</p>
          <p className="mt-1 text-sm text-slate-600">{data.partnershipDisputes.agreementExplain}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
            {data.partnershipDisputes.agreementBullets.map((a) => (
              <li key={a}>• {a}</li>
            ))}
          </ul>
          <p className="mt-6 text-xs font-black uppercase tracking-wider text-slate-500">{data.partnershipDisputes.recordsHeading}</p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
            {data.partnershipDisputes.records.map((r) => (
              <li key={r}>• {r}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-900/[0.08] bg-slate-950 p-6 text-white">
          <p className="text-xs font-black uppercase tracking-[0.2em]" style={{ color }}>
            {data.partnershipDisputes.ifDisputeHeading}
          </p>
          <ol className="mt-4 list-decimal space-y-3 pl-5 text-sm font-semibold text-white/90">
            {data.partnershipDisputes.ifDisputeSteps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <h3 className="text-xl font-black text-slate-950">{data.ownershipLiability.title}</h3>
        <p className="mt-6 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.ownershipLiability.separateHeading}
        </p>
        <p className="mt-3 whitespace-pre-line text-sm font-semibold leading-relaxed text-slate-800">
          {data.ownershipLiability.separateBody}
        </p>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.ownershipLiability.mattersHeading}</p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
          {data.ownershipLiability.mattersBullets.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col justify-center rounded-xl border border-amber-200/70 bg-amber-50/50 p-6 sm:p-8">
        <p className="text-sm font-black text-amber-950">{data.ownershipLiability.riskHeading}</p>
        <p className="mt-2 text-sm font-semibold text-amber-950/90">{data.ownershipLiability.riskLead}</p>
        <p className="mt-4 text-base font-black text-amber-950">{data.ownershipLiability.riskBullet}</p>
      </div>
    </section>

    <section className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
      <SectionHead eyebrow="Brand &amp; ideas" title={data.intellectualProperty.title} subtitle={data.intellectualProperty.intro} color={color} />
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.intellectualProperty.protectHeading}</p>
          <ul className="mt-2 space-y-1.5 text-sm font-semibold text-slate-800">
            {data.intellectualProperty.protect.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.intellectualProperty.whyHeading}
          </p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
            {data.intellectualProperty.whyBullets.map((w) => (
              <li key={w}>• {w}</li>
            ))}
          </ul>
          <p className="mt-4 border-l-4 pl-4 text-sm font-bold leading-relaxed text-slate-900" style={{ borderColor: color }}>
            {data.intellectualProperty.lawFootnote}
          </p>
        </div>
      </div>
    </section>

    <section className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-10">
      <SectionHead eyebrow="On the ground" title={data.commonProblemsGhana.title} subtitle={data.commonProblemsGhana.intro} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.commonProblemsGhana.issues.map((issue) => (
          <div
            key={issue}
            className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3 text-sm font-bold text-slate-800"
          >
            • {issue}
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Action list" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 sm:grid-cols-2">
        {[data.practicalTips.beforeStart, data.practicalTips.duringOps, data.practicalTips.whenPartnering, data.practicalTips.whenExpanding].map(
          (block) => (
            <motion.div
              key={block.heading}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm"
            >
              <p className="text-sm font-black text-slate-950">{block.heading}</p>
              <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-700">
                {block.items.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </motion.div>
          ),
        )}
      </div>
    </section>

    <section>
      <div className="overflow-hidden rounded-xl border border-red-200/80 bg-gradient-to-br from-red-50/90 to-white">
        <div className="flex items-start gap-3 border-b border-red-100 px-6 py-4 sm:px-8">
          <AlertTriangle className="mt-0.5 shrink-0 text-red-500" size={20} aria-hidden />
          <h3 className="text-lg font-black text-gray-950 sm:text-xl">{data.redFlags.title}</h3>
        </div>
        <div className="space-y-2 p-6 sm:p-8 sm:pt-4">
          {data.redFlags.items.map((flag) => (
            <div
              key={flag}
              className="rounded-lg border border-red-100/90 bg-white/90 px-4 py-3 text-sm font-semibold text-red-950"
            >
              {flag}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-6 sm:p-8">
      <SectionHead eyebrow="Editorial" title={data.editorialPlaybook.title} color={color} />
      <div className="mt-6 grid gap-6 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200/80 bg-white p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.articlesHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.articles.map((a) => (
              <li key={a}>• {a}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-slate-200/80 bg-white p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.videosHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.videos.map((v) => (
              <li key={v}>• {v}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-slate-200/80 bg-white p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.leadCtasHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[data.funnelApplication.hook, data.funnelApplication.education, data.funnelApplication.authority, data.funnelApplication.conversion].map(
          (step) => (
            <div key={step.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
                {step.label}
              </p>
              <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
            </div>
          ),
        )}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div
        className="absolute inset-0 opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
        aria-hidden
      />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <ul className="mt-6 space-y-4">
          {data.finalTakeaway.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-base font-bold leading-relaxed text-white/95 sm:text-lg">
              <CheckCircle2 size={22} className="shrink-0 opacity-90" style={{ color }} aria-hidden />
              {b}
            </li>
          ))}
        </ul>
        <div className="mt-10 border-t border-white/10 pt-8">
          <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.bottomLine.title}
          </p>
          <p className="mt-3 text-lg font-black text-white sm:text-xl">{data.bottomLine.lead}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
            <p className="rounded-lg border border-white/15 bg-white/[0.06] p-4 text-center text-sm font-bold text-white sm:text-base">
              {data.bottomLine.smooth}
            </p>
            <span className="hidden text-center text-sm font-black text-white/50 sm:block">{data.bottomLine.and}</span>
            <p className="rounded-lg border border-red-500/30 bg-red-950/35 p-4 text-center text-sm font-bold text-red-100 sm:text-base">
              {data.bottomLine.stressful}
            </p>
          </div>
          <p className="mt-6 flex flex-wrap items-center gap-2 text-sm font-semibold text-white/70">
            <ArrowRight size={16} style={{ color }} aria-hidden />
            {data.bottomLine.closing}
          </p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Common Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const BusinessCorporateArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead
          eyebrow="Content Library"
          title="Articles &amp; Guides"
          subtitle={data.funnelTagline}
          color={color}
        />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.editorialPlaybook.leadCtasHeading}
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const BusinessCorporateMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="bcl-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Business and corporate law videos"
            items={videos}
            header={<h3 id="bcl-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="bcl-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Business and corporate law podcast episodes"
            items={podcasts}
            header={<h3 id="bcl-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} aria-hidden />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold text-white/85">{data.primaryCtaHeading}</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
